import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Container, Input, Table } from "reactstrap";
import "./CommandeLine.css";
import { FaPlus, FaTrash } from "react-icons/fa";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { supplierActions } from "../../sagas/supplierSlice";
import { productActions } from "../../sagas/productSlice";
import { formatPrice, preventScroll } from "../../helpers/utility";

const CommandeLine = ({ formik, disabled }) => {
    const { values, setFieldValue } = formik;
    const [items, setItems] = useState([]);
    const [articleSearchByIndex, setArticleSearchByIndex] = useState({});
    const [activeSearchIndex, setActiveSearchIndex] = useState(null);
    const articleInputRefs = useRef({});

    const unites = useSelector((state) => state.products.unites);
    const { products, pageSize, loading } = useSelector((state) => state.products);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(productActions.findAllUnites());
    }, [dispatch]);

    useEffect(() => {
        setItems(
            values.lignes.map((line) => ({
                ...line,
                unite: unites.find((u) => u.code === line.unite)?.libelle || line.unite || "",
            }))
        );
    }, [unites, values.lignes]);

    const roundMoney = (value, decimals = 2) => {
        return Number(
            Math.round((Number(value) + Number.EPSILON) * Math.pow(10, decimals)) /
                Math.pow(10, decimals)
        );
    };

    const recalculateTotals = (newItems) => {
        let totalHT = 0;
        let totalTVA = 0;
        let totalTTC = 0;

        newItems.forEach((item) => {
            totalHT += Number(item.montantHT) || 0;
            totalTVA += Number(item.montantTVA) || 0;
            totalTTC += Number(item.montantTTC) || 0;
        });

        setFieldValue("lignes", newItems);
        setFieldValue("montantTotalHT", roundMoney(totalHT));
        setFieldValue("montantTotalTaxe", roundMoney(totalTVA));
        setFieldValue("montantTotalTTC", roundMoney(totalTTC));
    };

    const handleInputChange = (index, event) => {
        const { name, value } = event.target;
        const newItems = [...items];

        newItems[index] = {
            ...newItems[index],
            [name]: value,
        };

        const quantity = Number(newItems[index].quantiteCommandee) || 0;
        const unitPriceTTC = Number(newItems[index].prixUnitaireTTC) || 0;
        const tauxTVA = Number(newItems[index].tauxTVA) || 0;

        const unitPriceHT = tauxTVA > 0
            ? roundMoney(unitPriceTTC / (1 + tauxTVA))
            : roundMoney(unitPriceTTC);

        const montantHT = roundMoney(quantity * unitPriceHT);
        const montantTTC = roundMoney(quantity * unitPriceTTC);
        const montantTVA = roundMoney(montantTTC - montantHT);

        newItems[index].prixUnitaireHT = unitPriceHT;
        newItems[index].montantHT = montantHT;
        newItems[index].montantTTC = montantTTC;
        newItems[index].montantTVA = montantTVA;

        setItems(newItems);
        recalculateTotals(newItems);
    };

    const addNewLine = () => {
        const newItem = {
            articleId: null,
            articleNom: "",
            quantiteCommandee: 1,
            prixUnitaireHT: 0,
            prixUnitaireTTC: 0,
            unite: "",
            tauxTVA: 0.2,
            montantHT: 0,
            montantTTC: 0,
            montantTVA: 0,
        };

        const newItems = [...items, newItem];
        setItems(newItems);
        setFieldValue("lignes", newItems);
        setActiveSearchIndex(newItems.length - 1);
    };

    const handleDeleteItem = (index) => {
        const newItems = items.filter((_, itemIndex) => itemIndex !== index);
        setItems(newItems);
        recalculateTotals(newItems);
    };

    const debouncedArticleSearch = useMemo(
        () =>
            debounce((query) => {
                if (!query || !query.trim()) return;

                dispatch(
                    productActions.findAll({
                        search: query,
                        page: 0,
                        size: pageSize || 10,
                    })
                );
            }, 300),
        [dispatch, pageSize]
    );

    useEffect(() => {
        return () => debouncedArticleSearch.cancel();
    }, [debouncedArticleSearch]);

    useEffect(() => {
        if (activeSearchIndex === null) return;

        const inputEl = articleInputRefs.current[activeSearchIndex];
        if (inputEl) {
            inputEl.focus();
            inputEl.select?.();
        }
    }, [activeSearchIndex, items.length]);

    const handleArticleSearchChange = (index, value) => {
        setArticleSearchByIndex((prev) => ({ ...prev, [index]: value }));
        setActiveSearchIndex(index);
        debouncedArticleSearch(value);
    };

    const handleProductSelect = (product, lineIndex) => {
        if (!values.fournisseurId) {
            Swal.fire({
                icon: "warning",
                title: t("purchase.noFournisseurTitle"),
                text: t("purchase.noFournisseurMessage"),
                confirmButtonText: t("common.ok", "OK"),
            });
            return;
        }

        const productExists = items.some(
            (item, index) => index !== lineIndex && item.articleId === product.id
        );
        if (productExists) {
            Swal.fire({
                icon: "warning",
                title: t("purchase.productExistsTitle"),
                text: t("purchase.productExistsMessage"),
                confirmButtonText: t("common.ok"),
            });
            return;
        }

        dispatch(
            supplierActions.findSupplierArticlePrice({
                fournisseurId: values.fournisseurId,
                productId: product.id,
                onSuccess: (data) => {
                    const prixAchatHT = Number(data) || 0;
                    const tauxTVA = Number(product.tauxTVAAchat) || 0.2;
                    const prixUnitaireHT = roundMoney(prixAchatHT);
                    const prixUnitaireTTC = roundMoney(prixUnitaireHT * (1 + tauxTVA));

                    const newItem = {
                        articleId: product.id,
                        articleNom: product.libelle,
                        quantiteCommandee: 1,
                        prixUnitaireHT,
                        prixUnitaireTTC,
                        unite:
                            unites.find((u) => u.code === product.unite)?.libelle || "",
                        tauxTVA,
                        montantHT: prixUnitaireHT,
                        montantTTC: prixUnitaireTTC,
                        montantTVA: roundMoney(prixUnitaireTTC - prixUnitaireHT),
                    };

                    const newItems = [...items];
                    newItems[lineIndex] = newItem;

                    setItems(newItems);
                    recalculateTotals(newItems);
                    setArticleSearchByIndex((prev) => ({
                        ...prev,
                        [lineIndex]: product.libelle,
                    }));
                    setActiveSearchIndex(null);
                },
            })
        );
    };

    return (
        <Container fluid>
            <Table bordered>
                <thead>
                    <tr>
                        <th style={{ width: "420px", minWidth: "420px" }}>
                            {t("devis.article")}
                        </th>
                        <th>{t("devis.quantite")}</th>
                        <th>{t("devis.unite")}</th>
                        <th style={{ width: "200px" }}>{t("devis.prixUnitaireTTC")}</th>
                        <th>{t("devis.prixUnitaireHT")}</th>
                        <th>{t("devis.montantHT")}</th>
                        <th>{t("devis.tauxTVA")}</th>
                        <th>{t("devis.montantTTC")}</th>
                        <th>{t("common.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan="9" className="text-center py-4">
                                <div className="text-muted">{t("purchase.noItems")}</div>
                            </td>
                        </tr>
                    ) : (
                        items.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <div style={{ position: "relative" }}>
                                        <Input
                                            type="text"
                                            name="articleNom"
                                            value={
                                                item.articleId
                                                    ? item.articleNom
                                                    : articleSearchByIndex[index] ?? ""
                                            }
                                            onChange={(event) =>
                                                handleArticleSearchChange(index, event.target.value)
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }
                                            }}
                                            placeholder={
                                                item.articleId ? "" : t("product.searchPlaceholder")
                                            }
                                            disabled={disabled || Boolean(item.articleId)}
                                            innerRef={(el) => {
                                                if (el) articleInputRefs.current[index] = el;
                                            }}
                                        />
                                        {!item.articleId &&
                                            activeSearchIndex === index &&
                                            (articleSearchByIndex[index] ?? "").trim().length > 0 && (
                                                <div
                                                    className="bg-white border rounded shadow-sm"
                                                    style={{
                                                        position: "absolute",
                                                        top: "100%",
                                                        left: 0,
                                                        right: 0,
                                                        zIndex: 20,
                                                        maxHeight: "220px",
                                                        overflowY: "auto",
                                                    }}
                                                >
                                                    {loading ? (
                                                        <div className="p-2 text-muted small">
                                                            {t("common.loading", "Loading...")}
                                                        </div>
                                                    ) : !products || products.length === 0 ? (
                                                        <div className="p-2 text-muted small">
                                                            {t("product.noProducts")}
                                                        </div>
                                                    ) : (
                                                        products.map((product) => (
                                                            <button
                                                                key={product.id}
                                                                type="button"
                                                                className="w-100 text-start border-0 bg-white px-2 py-2"
                                                                onClick={() =>
                                                                    handleProductSelect(product, index)
                                                                }
                                                            >
                                                                <div className="fw-semibold">
                                                                    {product.libelle}
                                                                </div>
                                                                <div className="small text-muted">
                                                                    {product.code} -{" "}
                                                                    {formatPrice(product.prixAchatTTC)}
                                                                </div>
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        name="quantiteCommandee"
                                        value={item.quantiteCommandee}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                        className="no-spinner"
                                        onFocus={(e) => {
                                            e.target.select();
                                            e.target.addEventListener("wheel", preventScroll, {
                                                passive: false,
                                            });
                                        }}
                                        onBlur={(e) => {
                                            e.target.removeEventListener("wheel", preventScroll);
                                        }}
                                    />
                                </td>
                                <td>
                                    <Input type="text" name="unite" value={item.unite} disabled />
                                </td>
                                <td style={{ width: "100px" }}>
                                    <Input
                                        type="number"
                                        name="prixUnitaireTTC"
                                        value={item.prixUnitaireTTC}
                                        onChange={(event) => handleInputChange(index, event)}
                                        disabled={disabled}
                                        className="no-spinner"
                                        onFocus={(e) => {
                                            e.target.select();
                                            e.target.addEventListener("wheel", preventScroll, {
                                                passive: false,
                                            });
                                        }}
                                        onBlur={(e) => {
                                            e.target.removeEventListener("wheel", preventScroll);
                                        }}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        name="prixUnitaireHT"
                                        value={item.prixUnitaireHT}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        name="montantHT"
                                        value={item.montantHT}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Input
                                        name="tauxTVA"
                                        value={`${(item.tauxTVA * 100).toFixed(0)} %`}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        name="montantTTC"
                                        value={formatPrice(item.montantTTC, false)}
                                        disabled
                                    />
                                </td>
                                <td>
                                    <Button
                                        color="danger"
                                        onClick={() => handleDeleteItem(index)}
                                        size="sm"
                                        disabled={disabled}
                                    >
                                        <FaTrash />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            <Button
                color="primary"
                onClick={addNewLine}
                disabled={disabled}
                className="mb-3"
            >
                <FaPlus /> {t("facture.addLine")}
            </Button>
        </Container>
    );
};

export default CommandeLine;
